import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import * as img from 'src/assets/images'
import styles from './Sidebar.module.scss'
import { TFunction } from 'next-i18next'
import { PieChartOutlined } from '@ant-design/icons'

const marketUrl = process.env.MARKET_PLACE_URL

const hideSidebar = (route: string): boolean => {
  const completeRoutes = [
    '-users',
    '-withdrawals',
    '-documents',
    '-transfers',
    '-deposits',
    'metabase-data'
  ]
  return completeRoutes.some((r) => route.endsWith(r))
}

const getDefaultOpenKeys = (currentActiveKey: string): any => {
  if (['deposits', 'my-orders', 'my-sales'].includes(currentActiveKey)) {
    return ['Transactions']
  } else if (['stock-alerts', 'notifications'].includes(currentActiveKey)) {
    return ['Notifications']
  }
  return []
}

const getLink = (
  label: string,
  url: string,
  applyMargin: boolean,
  count: any = null
) => {
  const className = applyMargin
    ? `${styles['menu-text']} ${styles['title-margin']}`
    : styles['menu-text']
  return (
    <Link href={url} legacyBehavior>
      <a className={`${styles['menu-nuxt-link']} flex`}>
        <h3 className={className}>
          {label}
          {count > 0 && (
            <span className={styles.buble}>{count}</span>
          )}
        </h3>
      </a>
    </Link>
  )
}

const getImage = (iconFocus: string, label: string) => (
  <Image
    src={iconFocus}
    className={styles['icon-image']}
    width={15}
    height={15}
    alt={`icon-example-${label}`}
  />
)

const getTitle = (label: string) => (
  <h3 className={styles['title-margin']}>{label}</h3>
)

interface SidebarSection {
  key: string
  icon?: React.ReactNode
  children?: React.ReactNode
  subItems?: SidebarSection[]
  isVisible: boolean
  isSubMenu?: boolean
  title?: React.ReactNode
  className?: any
  hasPermision?: boolean
}

const menuSections = (
  t: TFunction,
  isAdminOrDeveloper: boolean,
  isWidgetUser: boolean,
  isAdminUser: boolean,
  currentActiveKey: string,
  isPremiumCalculatorUser: boolean,
  messagesCount: number
): SidebarSection[] => [
  {
    // Summary
    key: 'summary',
    icon: getImage(img.Dashboard, 'Dashboard'),
    children: getLink(t('menuItems.Summary'), `/summary`, true),
    isVisible: true
  },

  {
    // Transactions
    key: 'Transactions',
    title: getTitle(t('menuItems.TransactionSummary')),
    isSubMenu: true,
    icon: getImage(img.Transactions, 'Transactions'),
    className:
        ['deposits', 'my-orders', 'my-sales'].includes(currentActiveKey) &&
        styles.activeKey,
    isVisible: true,
    subItems: [
      {
        // Deposits
        key: 'deposits',
        children: getLink(t('menuItems.Deposits'), `/deposits`, false),
        isVisible: true
      },
      {
        // My Orders
        key: 'my-orders',
        children: getLink(t('menuItems.Orders'), `/my-orders`, false),
        isVisible: true
      },
      {
        // My Sales
        key: 'my-sales',
        children: getLink(t('menuItems.MySales'), `/my-sales`, false),
        isVisible: isAdminOrDeveloper
      }
    ]
  },

  {
    // My Projects
    key: 'my-projects',
    icon: getImage(img.Project, 'ProjectList'),
    children: getLink(t('menuItems.ProjectsList'), `/my-projects`, true),
    isVisible: isAdminOrDeveloper,
    hasPermision: true
  },

  {
    // Messages
    key: 'messages',
    icon: getImage(img.Email, 'Messages'),
    children: getLink(
      t('menuItems.Messages'),
      `/messages`,
      true,
      messagesCount
    ),
    isVisible: true
  },

  {
    // Stock Alerts
    key: 'stock-alerts',
    icon: getImage(img.Notifications, 'Transactions'),
    children: getLink(t('menuItems.Alerts'), `/stock-alerts`, true),
    isVisible: isWidgetUser
  },

  {
    // Offers
    key: 'offers',
    icon: getImage(img.Offer, 'Offers'),
    children: getLink(t('menuItems.Offers'), `/offers`, true),
    isVisible: true
  },

  {
    // Offers
    key: 'publications',
    icon: getImage(img.Publications, 'Publications'),
    children: getLink(t('Publications.Title'), `/publications`, true),
    isVisible: true
  },

  {
    // Premium Calculator
    key: 'premium-calculator',
    icon: getImage(img.PremiumCalculator, 'PremiumCalculator'),
    children: getLink(
      t('menuItems.PremiumCalculator'),
      `${marketUrl}/premium-calculator/home`,
      true
    ),
    isVisible: isAdminUser || isPremiumCalculatorUser
  },

  {
    // My Footprint
    key: 'my-footprint',
    icon: getImage(img.MyFootPrint, 'FootPrint'),
    children: getLink(t('menuItems.FootPrint'), `/my-footprint`, true),
    isVisible: true
  },

  {
    // Admin Section
    key: 'admin',
    icon: getImage(img.AdminIcon, 'Admin'),
    children: getLink(t('menuItems.Admin'), `/admin`, true),
    isVisible: isAdminUser,
    hasPermision: true
  },

  {
    // Developers
    key: 'developers',
    icon: getImage(img.Developers, 'Developers'),
    children: getLink(t('menuItems.Developers'), `/developers`, true),
    isVisible: isWidgetUser,
    hasPermision: true
  },
  { // Affiliate center
    key: 'affiliate-center',
    icon: <PieChartOutlined className={styles['custom-icon']} />,
    children: getLink(t('menuItems.AffiliateCenter'), `/affiliate-center`, false),
    isVisible: true,
    hasPermision: true
  },

  {
    // Settings
    key: 'settings',
    icon: getImage(img.Settings, 'Settings'),
    children: getLink(t('menuItems.Settings'), `/settings`, true),
    isVisible: true
  }
]

export const helpers = {
  getDefaultOpenKeys,
  getImage,
  getLink,
  getTitle,
  menuSections,
  hideSidebar
}
